import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { User, MessageCircle, Save, Edit, X } from 'lucide-react';

interface UserProfileProps {
  onClose?: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [telegramUserId, setTelegramUserId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await userAPI.getProfile();
      if (response.status === 'success') {
        setUserProfile(response.data);
        setTelegramUserId(response.data.telegram_user_id || '');
      } else {
        toast.error('Failed to load user profile');
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Failed to load user profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!telegramUserId.trim() && telegramUserId !== '') {
      toast.error('Please enter a valid Telegram user ID or leave it empty');
      return;
    }

    try {
      setIsSaving(true);
      const response = await userAPI.updateTelegramId(telegramUserId.trim());
      
      if (response.status === 'success') {
        toast.success('Telegram user ID updated successfully');
        setIsEditing(false);
        await fetchUserProfile(); // Refresh profile
      } else {
        toast.error(response.message || 'Failed to update Telegram user ID');
      }
    } catch (error: any) {
      console.error('Error updating Telegram user ID:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to update Telegram user ID');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTelegramUserId(userProfile?.telegram_user_id || '');
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2" />
            User Profile
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* User Info */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="h-12 w-12 rounded-full"
              />
            ) : (
              <div className="h-12 w-12 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="h-6 w-6 text-gray-600" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
              <p className="text-sm text-gray-600">{user?.email}</p>
            </div>
          </div>

          {userProfile && (
            <div className="text-sm text-gray-600">
              <p>Member since: {new Date(userProfile.added_at).toLocaleDateString()}</p>
              {userProfile.is_admin && (
                <p className="text-purple-600 font-medium mt-1">Administrator</p>
              )}
            </div>
          )}
        </div>

        {/* Telegram User ID Section */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              Telegram User ID
            </h3>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </button>
            )}
          </div>

          <div className="space-y-4">
            {isEditing ? (
              <div>
                <label htmlFor="telegramUserId" className="block text-sm font-medium text-gray-700 mb-2">
                  Telegram User ID
                </label>
                <input
                  type="text"
                  id="telegramUserId"
                  value={telegramUserId}
                  onChange={(e) => setTelegramUserId(e.target.value)}
                  placeholder="Enter your Telegram user ID (e.g., 123456789)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSaving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to remove your Telegram user ID. You can find your Telegram user ID by messaging @userinfobot on Telegram.
                </p>
                
                <div className="flex space-x-3 mt-4">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="bg-gray-50 rounded-md p-3">
                  {userProfile?.telegram_user_id ? (
                    <p className="text-sm text-gray-900">
                      <span className="font-medium">Current ID:</span> {userProfile.telegram_user_id}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No Telegram user ID set
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Your Telegram user ID is used for notifications. You can find your ID by messaging @userinfobot on Telegram.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Help Information */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <MessageCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-blue-800">How to find your Telegram User ID</h4>
              <ol className="text-sm text-blue-700 mt-1 list-decimal list-inside space-y-1">
                <li>Open Telegram and search for @userinfobot</li>
                <li>Start a chat with the bot</li>
                <li>Send any message to the bot</li>
                <li>The bot will reply with your user ID (a number)</li>
                <li>Copy that number and paste it here</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 